import { useMemo, useState } from 'react'
import { Box } from '@mui/material'
import { com } from '@eidu/web'
import usePaginatedEntitiesOfType from '../../io/usePaginatedEntitiesOfType'
import useEntityById from '../../io/useEntityById'
import useEntityTypes from '../../io/useEntityTypes'
import EqualityHashMap from '../../util/EqualityHashMap'
import EntityAutocomplete from './EntityAutocomplete'
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import EntitySelector = com.eidu.sharedlib.entity.selector.EntitySelector

type SingleInputProps = {
  entityTypeId: EntityTypeId
  selector: EntitySelector.Single | undefined
  setSelector: (selector: EntitySelector.Single) => void
  disabled: boolean
}

const SingleInput = ({ entityTypeId, selector, setSelector, disabled }: SingleInputProps) => {
  const [searchQuery, setSearchQuery] = useState('')
  const { entityTypes } = useEntityTypes()
  const { entity: selectedEntity, loading: selectedEntityLoading } = useEntityById(selector?.entityId)
  const selectedEntityMissing = selector !== undefined && !selectedEntityLoading && selectedEntity === null

  const { entities, loadMore, hasMore, loading } = usePaginatedEntitiesOfType({
    typeId: entityTypeId,
    searchQuery,
    allTypes: entityTypes ?? new Map(),
  })

  const entitiesById = useMemo(() => {
    const map = new EqualityHashMap(entities.map((e) => [e.entity.id, e]))
    if (selectedEntity) map.set(selectedEntity.entity.id, selectedEntity)
    return map
  }, [entities, selectedEntity])

  if (!entityTypes) return null

  return (
    <Box sx={{ minWidth: 240 }} data-testid="single-input">
      <EntityAutocomplete
        entitiesById={entitiesById}
        entityTypesById={entityTypes}
        value={selector?.entityId}
        onChange={(id) => id && setSelector(new EntitySelector.Single(entityTypeId, id))}
        onSearchChange={setSearchQuery}
        loadMore={loadMore}
        hasMore={hasMore}
        loading={loading}
        label="Entity"
        disabled={disabled}
        size="small"
        error={selectedEntityMissing}
      />
    </Box>
  )
}

export default SingleInput
